import { Injectable } from "@angular/core";
import { ActivatedRoute, Router, UrlTree } from "@angular/router";
import { Observable, take } from "rxjs";
import { AmountType, CheckoutQueryParamsCollection, CheckoutRouteName } from "../entities/checkout.model";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { ROUTER_OUTLET_NAME } from "@modules/+new/shared/universal-modal/entities/magic";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { Conversion, ErrorClass } from "@models";
import { CheckoutDepositMoreOptionsData } from "@modules/checkout/components/checkout-deposit-confirmation/checkout-deposit-more-options-data";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { MarginTradingRouteName } from "@modules/margin-trading/entities/margin-trading.model";
import { TRADABLES_TYPE } from "@helpers/constants";
import { convertFiatToTradable, convertTradableToFiat, toEuroByFiatId } from "@helpers/tradable-convert";
import { fixToPrecisionHelper, setPrecisionDecimals } from "@helpers/utils";
import { IAmountSelectionProperties } from "../entities/checkout.amount.model";
import { Tradable } from "@models/interfaces/tradable";

@Injectable({ providedIn: "root" })
export class CheckoutNavigationService {
	public constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService,
		private universalModalService: UniversalModalService,
		private readonly portfolioFacade: PortfolioFacade
	) {}

	gotoByNavigationUrl(path: CheckoutRouteName, queryParams?: Partial<CheckoutQueryParamsCollection>): void {
		const urlTree = this.gotoByUrlTree(path, queryParams);
		this.router.navigateByUrl(urlTree);
	}

	gotoByNavigationByUrlTree(path: UrlTree): void {
		this.router.navigateByUrl(path);
	}

	gotoByNavigation(
		path: CheckoutRouteName | MarginTradingRouteName,
		queryParams?: Partial<CheckoutQueryParamsCollection>
	): void {
		this.router.navigate([{ outlets: { [ROUTER_OUTLET_NAME]: [TRADE_TYPE.CHECKOUT, path] } }], { queryParams });
	}

	gotoByNavigationAsync(
		path: CheckoutRouteName | MarginTradingRouteName,
		queryParams?: Partial<CheckoutQueryParamsCollection>
	): Promise<boolean> {
		return this.router.navigate([{ outlets: { [ROUTER_OUTLET_NAME]: [TRADE_TYPE.CHECKOUT, path] } }], {
			queryParams
		});
	}

	// extra flexible navigation method for margin trading and checkout
	gotoByNavigationFlexi<P, Q = CheckoutQueryParamsCollection>(path: P, queryParams?: Partial<Q>): void {
		this.router.navigate([{ outlets: { [ROUTER_OUTLET_NAME]: [TRADE_TYPE.CHECKOUT, path] } }], { queryParams });
	}

	async gotoByNavigationRefresh(
		path: CheckoutRouteName | MarginTradingRouteName,
		queryParams?: Partial<CheckoutQueryParamsCollection>
	): Promise<boolean> {
		return this.router.navigate([{ outlets: { [ROUTER_OUTLET_NAME]: [TRADE_TYPE.CHECKOUT, path] } }], {
			queryParams,
			replaceUrl: true
		});
	}

	gotoOutsideModalWithLink(path: string): void {
		this.closeModal();
		this.router.navigateByUrl(path).then(() => {
			this.portfolioFacade.changeRefreshPortfolioStatus(true);
		});
	}

	gotoAnotherModalWithLink(destination: string, path: string): void {
		this.router.navigate([{ outlets: { [ROUTER_OUTLET_NAME]: [destination, path] } }]);
	}

	gotoDepositAmountScreen(destination: string, path: string, params: CheckoutDepositMoreOptionsData): void {
		this.router.navigate([{ outlets: { [ROUTER_OUTLET_NAME]: [destination, path] } }], { queryParams: params });
	}

	gotoByUrlTree(path: CheckoutRouteName, queryParams?: Partial<CheckoutQueryParamsCollection>): UrlTree {
		return this.router.createUrlTree([{ outlets: { [ROUTER_OUTLET_NAME]: [TRADE_TYPE.CHECKOUT, path] } }], {
			queryParams
		});
	}

	// extra flexible urlTree creation method for margin trading and checkout
	gotoByUrlTreeFlexi<P, Q = CheckoutQueryParamsCollection>(path: P, queryParams?: Partial<Q>): UrlTree {
		return this.router.createUrlTree([{ outlets: { [ROUTER_OUTLET_NAME]: [TRADE_TYPE.CHECKOUT, path] } }], {
			queryParams
		});
	}

	routeParamsReader(): Observable<Partial<CheckoutQueryParamsCollection>> {
		return this.activatedRoute.queryParams;
	}

	closeModal(): void {
		this.universalModalService.close(ModalCloseReason.close);
	}

	goBackToAssetSelectionCausedByError(error?: ErrorClass | Error): UrlTree {
		let errorMessage = this.translationService.translate("cash_plus_opt_error_message");
		if (error) {
			if (error instanceof ErrorClass && error.responseErrors[0]) {
				if (error?.responseErrors[0]?.title) {
					errorMessage = error?.responseErrors[0].title;
				} else {
					errorMessage = error?.message;
				}
			} else {
				errorMessage = error?.message;
			}
		}

		this.infoService.setError(errorMessage);
		return this.gotoByUrlTree(CheckoutRouteName.AssetSelect);
	}

	gotoDepositNow(): UrlTree {
		return this.router.createUrlTree([{ outlets: { modal: ["payments", "deposit-now"] } }]);
	}

	switchUnitNavigation(
		amount: number,
		vm: Partial<IAmountSelectionProperties>,
		path: CheckoutRouteName | MarginTradingRouteName = CheckoutRouteName.AmountSelect,
		stableCoin: Tradable = null
	): void {
		const { fiat, legacyTradable, priceConversions, tradable, isFiatSelected } = vm;

		const convertedToEur = toEuroByFiatId(+priceConversions.price.getPrice().getFiat(), fiat);

		let _maxFiat = stableCoin ? convertedToEur.toString() : priceConversions.price.getPrice().getFiat();

		const conversion = new Conversion();
		conversion.setFiatAmount(_maxFiat);
		let _amount: number;
		if (isFiatSelected) {
			//if fiat is selected convert to tradable
			const converted = convertFiatToTradable(fiat, amount, conversion);
			_amount =
				tradable.type === TRADABLES_TYPE.INDEX
					? amount
					: fixToPrecisionHelper(converted, legacyTradable.getPrecisionForTx());
		} else {
			const converted = convertTradableToFiat(conversion, amount, fiat);
			// otherwise convert to fiat
			_amount = tradable.type === TRADABLES_TYPE.INDEX ? amount : +setPrecisionDecimals(converted.toString(), 2);
		}

		// update the state of the viewmodel itself to update the UI
		vm.isFiatSelected = !isFiatSelected;
		vm.amount = _amount;

		const queryParams = isFiatSelected
			? { selectedAmountType: AmountType.asset, amount: _amount }
			: { selectedAmountType: AmountType.fiat, amount: _amount };

		//persisiting the query params with latest update
		this.routeParamsReader()
			.pipe(take(1))
			.subscribe((query) => {
				this.gotoByNavigationRefresh(path, {
					...query,
					...queryParams
				});
			});
	}
}
